import { Component, h, Host } from '@stencil/core';
import { Element, HTMLStencilElement, Prop, State } from '@stencil/core/internal';
import { WindowResizeService } from '../../services/window-resize-service';
import { ResponsiveString } from '../../typings';
import { getCurrentValue, getHydratedClassString } from '../../utils/utils';
import videojs from 'video.js';
import * as videojsyoutube from 'videojs-youtube';

export type SupportedVideoMethods = 'html5' | 'youtube';

videojsyoutube; // needed to ensure videojs youtube loads

/**
 * @slot poster - Poster shown before video is played.
 * @slot playButton - Play button contents.
 * @slot no-js-message - Contents of no JS message.
 * @slot content - Adjacent to no-js message for video. Usually not needed.
 * @slot - Default slot, same as content slot.
 */
@Component({
  tag: 'content-video',
  styleUrls: ['content-video.scss'],
  shadow: false,
})
export class ContentVideo {
  public player: any;
  videoElement!: HTMLVideoElement;
  @Element() hostElement: HTMLStencilElement;
  /**
   * @type Select
   * @options youtube
   * @options html5
   */
  @Prop() method: SupportedVideoMethods = 'youtube';
  /**
   * If you're using this inside a content-overlay for example, you may want the video to play immediately on launch.
   * @type Boolean
   */
  @Prop() autoplay: boolean = false;
  /**
   * Sets whether or not an autoplaying video should be muted. 
   * By default autoplay videos will be muted.
   * @type Boolean
   */
  @Prop() autoplayMute: boolean = true;
  /**
   * Supports YouTube and self hosted URLs.
   * @type String
   */
  @Prop() src: string = '';
  /**
   * @type Number
   */
  @Prop() width: number = 16;
  /**
   * @type Number
   */
  @Prop() height: number = 9;
  /**
   * @type String
   */
  @Prop() type: string = 'video/mp4';
  /**
   * @type String
   */
  @Prop() playButtonAriaLabel: string = 'Play';
  /**
   * @type String
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  classes: ResponsiveString;
  @State() classString;
  /**
   * @type Boolean
   */
  @Prop() controls: boolean = true;
  /**
   * @type String
   */
  @Prop() preload: string = 'metadata';
  /**
   * Fit the container this video is placed in. When the video is used in an overlay or another place where the video will takeover the existing height, it is recommended to enable this option.
   * @responsive
   * @type String
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  fitContainer: ResponsiveString = 'false';
  @State() isLoaded: boolean = false;
  @State() hasPoster: boolean = false;
  @State() isPlayed: boolean = false;

  computedVideoConfig() {
    let videoConfig = {};
    videoConfig = {
      fill: getCurrentValue(this.fitContainer) === 'true',
      fluid: getCurrentValue(this.fitContainer) === 'false',
      autoplay: this.autoplay === true ? (this.autoplayMute ? 'muted' : 'any') : false,
      techOrder: ['youtube'],
      src: this.src,
      sources: [
        {
          type: 'video/youtube',
          src: this.src,
        },
      ],
      youtube: {
        showinfo: 0,
        rel: 0,
        modestbranding: 1,
      },
    };
    console.log(videoConfig);
    return videoConfig;
  }

  initVideo() {
    this.isLoaded = true;
  }

  componentWillLoad() {
    WindowResizeService.registerProp(this.classes).subscribe(newValue => (this.classString = newValue));
  }

  componentDidLoad() {
    let self = this;
    let options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    };
    
    this.player = videojs(this.videoElement);
    // let observer = new IntersectionObserver(entries => {
    //   entries.forEach(async entry => {
    //     if (entry.isIntersecting && !this.player) {
    //       // if (!videojs) {
    //       //   videojs = await import('video.js');
    //       //   await import('videojs-youtube/dist/Youtube.js');
    //       // }
    //       // this.hasPoster = !!this.hostElement.querySelector('[slot="poster"]');
    //       // if (this.videoElement && !this.player) {
    //         // this.player = videojs.default(this.videoElement, this.computedVideoConfig(), function onPlayerReady() {
    //         //   self.initVideo();
    //         //   if (self.hasPoster || self.autoplay) {
    //         //     self.isPlayed = true;
    //         //     setTimeout(() => this.play(), 1);
    //         //   }
    //         // });
    //       // }
    //     }
    //   });
    // }, options);
    // observer.observe(this.hostElement);
  }

  onClickHandler(event: MouseEvent) {
    event.preventDefault();
    if (!this.isLoaded) {
      return false;
    }
    this.initVideo();
    this.isPlayed = true;
    this.player.play();
    if (this.method === 'html5') {
      this.videoElement.play();
    }
  }

  render() {
    let showPlayButton = !this.autoplay && ((this.hasPoster && !this.isPlayed) || !this.isPlayed);
    return (
      <Host class={this.classString}>
        {this.hasPoster && !this.isLoaded && (
          <div class="content-video__poster">
            <slot name="poster"></slot>
          </div>
        )}
        {showPlayButton && (
          <button class="content-video__play" aria-label={this.playButtonAriaLabel} onClick={ev => this.onClickHandler(ev)}>
            <slot name="playButton"></slot>
          </button>
        )}
        <div class="content-video__content">
          {(!this.hasPoster || this.isLoaded) && (
            <video
              ref={element => (this.videoElement = element)}
              class="video-js vjs-content-block-theme"
              controls={this.controls}
              preload={this.preload}
              width={this.width}
              height={this.height}
              crossorigin="anonymous"
              data-setup={JSON.stringify(this.computedVideoConfig())}
            >
              <slot name="no-js-message"></slot>
              <slot name="content"></slot>
              <slot></slot>
            </video>
          )}
        </div>
      </Host>
    );
  }
}
